<template>
  <popup-dialog
    :visible="visible"
    :close-button-visible="true"
    :title="$t('labels.importModel')"
    @show="onShow"
    @close="close"
  >
    <form ref="dropZoneRef" class="p-6" @submit.prevent="importFile()">
      <p style="text-align: center" class="mt-1">
        {{ $t("headings.importModel") }}
      </p>

      <button-upload
        id="fileinput-importpopup"
        ref="fileInput"
        accept=".model,.sim,application/simcisionmodel"
        class="popup-importmodel__filechooser"
        :class="{ 'popup-importmodel__filechooser--active': isOverDropZone }"
        @select-file="setFile($event)"
      >
        {{ $t("labels.browsePC") }}
      </button-upload>

      <div class="column">
        <input-text
          :label="$t('labels.name')"
          :value="name"
          @update:value="setNameManually($event)"
        />
        <input-texteditor
          :label="$t('labels.description')"
          :value="description"
          @update:value="description = $event"
        />
      </div>

      <div class="popup__buttons">
        <button class="button" :disabled="!allDataSet || importing" type="submit">
          <template v-if="importing">
            {{ $t("messages.importing") }}
          </template>
          <template v-else>
            {{ $t("labels.import") }}
          </template>
        </button>
      </div>
    </form>
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { saveModel, importModel } from "@/api/SaveService";
import envOptions from "@/helpers/Env";
import { getRouteToModelData } from "@core/routes/routeDetector";
import { showError } from "@core/ui/alert/alert";
import { useDropZone } from "@vueuse/core";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputText from "@core/input/InputText.vue";
import ButtonUpload from "@core/input/ButtonUpload.vue";

import type { Model } from "@/api/Model";

const props = withDefaults(defineProps<{
  folderId: number;
  workspaceId: number;
  visible?: boolean;
}>(), {
  visible: false
});

const emit = defineEmits(["close"]);

const dropZoneRef = ref<HTMLElement>();
const fileInput = ref<InstanceType<typeof ButtonUpload>>();

const router = useRouter();

const importing = ref(false);
const file = ref<File | null>(null);
const name = ref("");
const description = ref("");
const nameTouched = ref(false);

function setFile(newFile: File | null) {
  file.value = newFile;
  fillFileName(stripFiletype(newFile?.name || ""));
}
function fillFileName(fileName: string) {
  if (nameTouched.value == false) {
    name.value = fileName;
  }
}
function stripFiletype(fileName: string) {
  const endings = [".model", ".sim"];
  for (const ending of endings) {
    if (!fileName.endsWith(ending)) continue;
    const endPos = fileName.indexOf(ending);
    return fileName.substring(0, endPos);
  }
  return fileName;
}

const { isOverDropZone } = useDropZone(
  dropZoneRef,
  (files: File[] | null) => {
    if (!files) {
      setFile(null);
    } else {
      setFile(files[0]);
    }

    if (fileInput.value) {
      fileInput.value.overrideFiles(files);
    }
  }
);

const allDataSet = computed(() => {
  return file.value != null && nameFilledOut.value;
});
const nameFilledOut = computed(() => {
  return name.value && name.value.trim() != "";
});

function onShow() {
  file.value = null;
  name.value = "";
  description.value = "";
}

async function importFile() {
  if (allDataSet.value == false) return;
  if (file.value == null) {
    return;
  }

  importing.value = true;

  const modelInfo: Partial<Model> = {
    name: name.value,
    description: description.value,
    workspace: props.workspaceId,
    folder: props.folderId,
    permissions: {
      canDelete: true,
      canDownload: true,
      canFork: true,
      canLock: true,
      canUpdate: true
    }
  };

  try {
    await importModel(file.value, modelInfo);

    const { modelInfo: loadedModelInfo } = await saveModel({
      shouldRouteUpdate: false
    });

    const newRoute = getRouteToModelData(loadedModelInfo);
    if (envOptions.openModelsInNewTab) {
      const resolvedRoute = router.resolve(newRoute);
      window.open(resolvedRoute.href, "_blank");
    } else {
      router.push(newRoute);
    }

    close();
  } catch (error) {
    console.error(error);
    showError(error as Error);
  } finally {
    importing.value = false;
  }
}
function close() {
  emit("close");
}
function setNameManually(newName: string) {
  nameTouched.value = true;
  name.value = newName;
}
</script>

<style lang="scss">
.popup-importmodel {
  &__filechooser {
    margin-top: 2rem;
    margin-bottom: 1rem;

    &--active {
      outline: 1px solid var(--color-action);
    }
  }
  &__file {
    padding: 0.5rem;
    margin-right: 0.5rem;
    border: 1px solid var(--color-border-light);
    width: 100%;
    border-radius: 3px;
    &:hover {
      border-color: var(--color-text-lighter);
      box-shadow: 0 0 2px var(--color-text-lighter);
    }
  }
}
</style>
